import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  FaArrowLeft,
  FaComments,
  FaTrashAlt,
  FaUpload,
  FaPaperPlane
} from "react-icons/fa";
import config from "../config";
import PatientSidebarShell from "./PatientSidebarShell";

const VIEW_CONFIGS = "configs";
const VIEW_CHAT = "chat";

const AgentsPage = () => {
  const [view, setView] = useState(VIEW_CONFIGS);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 980);

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [selectedAgentSlug, setSelectedAgentSlug] = useState("");

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [importError, setImportError] = useState("");
  const [deletingSlug, setDeletingSlug] = useState("");
  const [agentToDelete, setAgentToDelete] = useState(null);

  const [elderId, setElderId] = useState("");
  const [threadId, setThreadId] = useState(null);
  const [chatThreads, setChatThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendStartedAt, setSendStartedAt] = useState(null);
  const [sendElapsedSeconds, setSendElapsedSeconds] = useState(0);
  const [chatError, setChatError] = useState("");
  const [threadToDelete, setThreadToDelete] = useState(null);

  const token = useMemo(() => localStorage.getItem("token"), []);
  const patientID = useMemo(() => localStorage.getItem("patientID"), []);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.slug === selectedAgentSlug) || null,
    [agents, selectedAgentSlug]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 980);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isSending || !sendStartedAt) {
      setSendElapsedSeconds(0);
      return;
    }

    const tick = () => {
      setSendElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - sendStartedAt) / 1000))
      );
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isSending, sendStartedAt]);

  const fetchDefaultElderId = useCallback(async () => {
    if (!patientID) return;

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/patient/profile/${patientID}`
      );
      const profileEmail = String(res.data?.email || "").trim();
      if (profileEmail) {
        setElderId(profileEmail);
        return;
      }
    } catch {
      // fall back below
    }

    setElderId(patientID);
  }, [patientID]);

  const fetchAgents = useCallback(async () => {
    if (!token) {
      setImportError("Login required to view imported agents.");
      setLoadingAgents(false);
      return;
    }

    setImportError("");

    try {
      const res = await axios.get(`${config.API_BASE_URL}${config.AGENTS.LIST}`, {
        headers: authHeaders
      });
      const nextAgents = Array.isArray(res.data?.agents) ? res.data.agents : [];
      setAgents(nextAgents);

      setSelectedAgentSlug((currentSlug) => {
        if (currentSlug && nextAgents.some((agent) => agent.slug === currentSlug)) {
          return currentSlug;
        }
        return nextAgents[0]?.slug || "";
      });
    } catch (err) {
      setImportError(err.response?.data?.message || "Failed to load agents.");
    } finally {
      setLoadingAgents(false);
    }
  }, [authHeaders, token]);

  const fetchThreadList = useCallback(async () => {
    if (!token || !selectedAgentSlug || !elderId.trim()) {
      setChatThreads([]);
      return;
    }

    setThreadsLoading(true);

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}${config.AGENTS.CHAT_THREADS}`,
        {
          params: {
            slug: selectedAgentSlug,
            elder_id: elderId.trim()
          },
          headers: authHeaders
        }
      );

      setChatThreads(Array.isArray(res.data?.sessions) ? res.data.sessions : []);
    } catch (err) {
      setChatError(err.response?.data?.message || "Failed to load chat threads.");
      setChatThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  }, [authHeaders, elderId, selectedAgentSlug, token]);

  const fetchHistory = useCallback(
    async (targetThreadId = null) => {
      if (!token || !selectedAgentSlug || !elderId.trim()) {
        setThreadId(null);
        setMessages([]);
        return;
      }

      setHistoryLoading(true);
      setChatError("");

      try {
        const params = {
          slug: selectedAgentSlug,
          elder_id: elderId.trim()
        };
        if (targetThreadId) {
          params.thread_id = targetThreadId;
        }

        const res = await axios.get(
          `${config.API_BASE_URL}${config.AGENTS.CHAT_HISTORY}`,
          {
            params,
            headers: authHeaders
          }
        );

        setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
        setThreadId(res.data?.session?.thread_id || null);
      } catch (err) {
        setChatError(err.response?.data?.message || "Failed to load chat history.");
        setMessages([]);
        setThreadId(null);
      } finally {
        setHistoryLoading(false);
      }
    },
    [authHeaders, elderId, selectedAgentSlug, token]
  );

  useEffect(() => {
    fetchAgents();
    fetchDefaultElderId();
  }, [fetchAgents, fetchDefaultElderId]);

  useEffect(() => {
    if (view !== VIEW_CHAT) return;

    let cancelled = false;

    const loadChatState = async () => {
      await fetchThreadList();
      if (cancelled) return;
      await fetchHistory(null);
    };

    loadChatState();

    return () => {
      cancelled = true;
    };
  }, [fetchHistory, fetchThreadList, view]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setImportStatus("");
    setImportError("");

    if (!file) {
      setSelectedFileName("");
      setSelectedPackage(null);
      return;
    }

    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw);
      setSelectedFileName(file.name);
      setSelectedPackage(parsed);
    } catch {
      setSelectedFileName(file.name);
      setSelectedPackage(null);
      setImportError("Invalid JSON file. Please select a valid agent package.");
    }
  };

  const handleImport = async () => {
    if (!token) {
      setImportError("Login required to import agents.");
      return;
    }

    if (!selectedPackage) {
      setImportError("Choose a valid JSON package first.");
      return;
    }

    setImportError("");
    setImportStatus("");
    setIsImporting(true);

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}${config.AGENTS.IMPORT}`,
        { package: selectedPackage },
        { headers: authHeaders }
      );

      setImportStatus(res.data?.message || "Agent imported.");
      setSelectedPackage(null);
      setSelectedFileName("");
      await fetchAgents();

      if (res.data?.agent?.slug) {
        setSelectedAgentSlug(res.data.agent.slug);
      }
    } catch (err) {
      const details = err.response?.data?.errors;
      setImportError(
        Array.isArray(details) && details.length
          ? details.join(" ")
          : err.response?.data?.message || "Failed to import package."
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleDeleteConfigClick = (agent) => {
    setAgentToDelete(agent);
  };

  const confirmDeleteConfig = async () => {
    if (!agentToDelete) return;
    const slug = String(agentToDelete.slug || "").trim();
    if (!slug) return;

    const name = String(agentToDelete.name || "this configuration");
    setImportStatus("");
    setImportError("");
    setDeletingSlug(slug);

    try {
      await axios.delete(`${config.API_BASE_URL}${config.AGENTS.DELETE(slug)}`, {
        headers: authHeaders
      });

      if (selectedAgentSlug === slug) {
        setSelectedAgentSlug("");
        setThreadId(null);
        setMessages([]);
      }

      await fetchAgents();
      setImportStatus(`Deleted configuration: ${name}`);
    } catch (err) {
      setImportError(
        err.response?.data?.message || "Failed to delete configuration."
      );
    } finally {
      setDeletingSlug("");
      setAgentToDelete(null);
    }
  };

  const cancelDeleteConfig = () => {
    setAgentToDelete(null);
  };

  const handleSendMessage = async () => {
    if (!token || !selectedAgent) return;

    const trimmedInput = messageInput.trim();
    const trimmedElderId = elderId.trim();

    if (!trimmedElderId) {
      setChatError("elder_id is required.");
      return;
    }

    if (!trimmedInput) {
      setChatError("Type a message before sending.");
      return;
    }

    const optimisticUserMessage = {
      id: `local-user-${Date.now()}`,
      role: "user",
      content: trimmedInput,
      created_at: new Date().toISOString()
    };
    const optimisticAssistantMessage = {
      id: `local-assistant-${Date.now()}`,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
      pending: true
    };

    const previousMessages = [...messages];
    setIsSending(true);
    setSendStartedAt(Date.now());
    setChatError("");
    setMessages((prev) => [...prev, optimisticUserMessage, optimisticAssistantMessage]);
    setMessageInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    try {
      const payload = {
        slug: selectedAgent.slug,
        input: trimmedInput,
        elder_id: trimmedElderId
      };

      if (threadId !== null && threadId !== "") {
        payload.thread_id = threadId;
      }

      const res = await axios.post(
        `${config.API_BASE_URL}${config.AGENTS.CHAT_RUN}`,
        payload,
        { headers: authHeaders }
      );

      setThreadId(res.data?.thread_id || null);
      setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
      await fetchThreadList();
    } catch (err) {
      const errorMessage =
        err.response?.data?.provider_error ||
        err.response?.data?.message ||
        "Failed to send message.";
      const timeoutHint = /timed out/i.test(String(errorMessage || ""))
        ? " The selected model is taking too long. Try again or switch to a faster model."
        : "";

      setMessages(previousMessages);
      setMessageInput(trimmedInput);
      setChatError(`${errorMessage}${timeoutHint}`);
    } finally {
      setIsSending(false);
      setSendStartedAt(null);
      setSendElapsedSeconds(0);
    }
  };

  const onComposerKeyDown = (event) => {
    const shouldSend =
      event.key === "Enter" && (event.ctrlKey || event.metaKey);

    if (shouldSend) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const openAgentChat = (slug) => {
    setSelectedAgentSlug(slug);
    setChatThreads([]);
    setThreadId(null);
    setMessages([]);
    setMessageInput("");
    setChatError("");
    setView(VIEW_CHAT);
  };

  const handleStartNewThread = () => {
    setThreadId(null);
    setMessages([]);
    setChatError("");
    setMessageInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleDeleteThreadClick = (targetThreadId, e) => {
    if (e) e.stopPropagation();
    setThreadToDelete(targetThreadId);
  };

  const confirmDeleteThread = async () => {
    if (!threadToDelete) return;
    const targetThreadId = threadToDelete;

    try {
      await axios.delete(`${config.API_BASE_URL}${config.AGENTS.DELETE_THREAD}`, {
        headers: authHeaders,
        params: {
          slug: selectedAgentSlug,
          elder_id: elderId.trim(),
          thread_id: targetThreadId
        }
      });

      setChatThreads((prev) => prev.filter(t => t.thread_id !== targetThreadId));

      if (threadId === targetThreadId) {
        handleStartNewThread();
      }
    } catch (err) {
      setChatError(err.response?.data?.message || "Failed to delete thread.");
    } finally {
      setThreadToDelete(null);
    }
  };

  const cancelDeleteThread = () => {
    setThreadToDelete(null);
  };

  const handleSelectThread = (nextThreadId) => {
    const normalized = String(nextThreadId || "").trim();
    if (!normalized || normalized === threadId) return;
    fetchHistory(normalized);
  };

  const canSend =
    Boolean(selectedAgent) &&
    Boolean(elderId.trim()) &&
    Boolean(messageInput.trim()) &&
    !isSending;

  const hasActiveConversation = Boolean(threadId !== null && threadId !== "");
  const orderedThreads = useMemo(
    () => (Array.isArray(chatThreads) ? chatThreads : []),
    [chatThreads]
  );
  const textareaRef = useRef(null);

  const handleInput = (e) => {
    setMessageInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <PatientSidebarShell activeItem="agents">
      {view === VIEW_CONFIGS ? (
        <>
          <div style={pageHeader}>
            <h1 style={title}>Agents</h1>
            <p style={subtitle}>
              Manage imported configurations, then open one for conversation.
            </p>
          </div>

          <div style={configsLayout}>
            <section style={card}>
              <h2 style={sectionTitle}>Import Agent Configuration</h2>
              <p style={helperText}>
                Upload a `kinkeepers_agent` JSON export. Existing imports are
                updated in place.
              </p>

              <div style={importControls}>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  style={primaryButton}
                  onClick={handleImport}
                  disabled={isImporting || !selectedPackage}
                >
                  <FaUpload /> {isImporting ? "Importing..." : "Import"}
                </button>
              </div>

              {selectedFileName ? (
                <p style={statusText}>Selected: {selectedFileName}</p>
              ) : null}
              {importStatus ? <p style={successText}>{importStatus}</p> : null}
              {importError ? <p style={errorText}>{importError}</p> : null}
            </section>

            <section style={card}>
              <h2 style={sectionTitle}>Saved Agent Configurations</h2>
              {loadingAgents ? <p style={helperText}>Loading...</p> : null}

              {!loadingAgents && agents.length === 0 ? (
                <p style={helperText}>No agents imported yet.</p>
              ) : null}

              <div style={agentGrid}>
                {agents.map((agent) => (
                  <article
                    key={agent.slug}
                    style={{
                      ...agentCard,
                      ...(selectedAgentSlug === agent.slug ? selectedAgentCard : null)
                    }}
                  >
                    <button
                      type="button"
                      style={agentMainButton}
                      onClick={() => openAgentChat(agent.slug)}
                    >
                      <div style={agentHeadingRow}>
                        <h3 style={agentName}>{agent.name}</h3>
                      </div>
                      <p style={agentDescription}>
                        {agent.description || "No description provided."}
                      </p>
                      <div style={agentMetaRow}>
                        <span>Model: {agent.model || "N/A"}</span>
                        <span>Tools: {agent.tools?.length || 0}</span>
                        <span>Version: {agent.version || "N/A"}</span>
                      </div>
                    </button>

                    <div style={agentActions}>
                      <button
                        type="button"
                        style={openChatButton}
                        onClick={() => openAgentChat(agent.slug)}
                      >
                        <FaComments /> Open Chat
                      </button>
                      <button
                        type="button"
                        style={deleteButton}
                        onClick={() => handleDeleteConfigClick(agent)}
                        disabled={deletingSlug === agent.slug}
                      >
                        <FaTrashAlt />
                        {deletingSlug === agent.slug ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>

          {agentToDelete && (
            <div style={modalOverlay}>
              <div style={modalCard}>
                <h3 style={modalTitle}>Delete Agent Configuration</h3>
                <p style={modalText}>
                  Are you sure you want to delete the configuration for "<strong>{agentToDelete.name}</strong>"? This action cannot be undone.
                </p>
                <div style={modalActions}>
                  <button
                    type="button"
                    style={modalCancelButton}
                    onClick={cancelDeleteConfig}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={modalDeleteButton}
                    onClick={confirmDeleteConfig}
                    disabled={deletingSlug === agentToDelete.slug}
                  >
                    {deletingSlug === agentToDelete.slug ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <section style={chatShell}>
          <div style={chatHeader}>
            <button
              type="button"
              style={backButton}
              onClick={() => setView(VIEW_CONFIGS)}
            >
              <FaArrowLeft /> Back
            </button>

            <div style={chatContext}>
              <div style={chatTitleRow}>
                <h2 style={chatTitle}>{selectedAgent?.name || "Agent Chat"}</h2>
                <span style={statusBadge}>
                  {hasActiveConversation ? "Active Session" : "New Session"}
                </span>
              </div>
              <p style={chatSubtitle}>
                {selectedAgent?.description || "No description provided."}
              </p>
            </div>
          </div>

          {!selectedAgent ? (
            <p style={helperText}>
              No selected agent. Return to configurations and pick one.
            </p>
          ) : (
            <>
              <div style={{ ...chatBody, ...(isMobile ? chatBodyMobile : null) }}>
                <div style={{ ...chatSidebar, ...(isMobile ? chatSidebarMobile : null) }}>
                  <div style={chatControls}>
                    <label style={fieldLabel}>
                      Elder ID / Email
                      <input
                        style={textInput}
                        value={elderId}
                        onChange={(event) => setElderId(event.target.value)}
                        placeholder="resident email or elder identifier"
                      />
                    </label>

                    <div style={helperPill}>
                      First message creates a new thread. Follow-ups automatically continue. Use <strong>Ctrl+Enter</strong> to send.
                    </div>

                    <div style={threadActionsRow}>
                      <button
                        type="button"
                        style={secondaryActionButton}
                        onClick={handleStartNewThread}
                        disabled={isSending}
                      >
                        Start New Thread
                      </button>
                      <span style={threadMetaText}>
                        {threadsLoading
                          ? "Loading threads..."
                          : `${orderedThreads.length} thread${orderedThreads.length === 1 ? "" : "s"
                          }`}
                      </span>
                    </div>
                  </div>

                  <div style={{ ...threadsPanel, ...(isMobile ? threadsPanelMobile : null) }}>
                    <div style={threadsPanelHeader}>
                      <span style={threadsPanelTitle}>Past Threads</span>
                    </div>
                    {threadsLoading ? (
                      <p style={helperText}>Loading thread list...</p>
                    ) : null}
                    {!threadsLoading && orderedThreads.length === 0 ? (
                      <p style={helperText}>No previous threads yet.</p>
                    ) : null}
                    {!threadsLoading && orderedThreads.length > 0 ? (
                      <div style={threadsList}>
                        {orderedThreads.map((session, index) => {
                          const isActive = threadId && session.thread_id === threadId;
                          const label = session.updated_at
                            ? new Date(session.updated_at).toLocaleString()
                            : `Thread ${index + 1}`;
                          return (
                            <div
                              key={session.id || session.thread_id || `${session.slug}-${index}`}
                              style={{
                                ...threadItemWrapper,
                                ...(isActive ? threadItemWrapperActive : null)
                              }}
                            >
                              <button
                                type="button"
                                style={threadItemMainButton}
                                onClick={() => handleSelectThread(session.thread_id)}
                                disabled={isSending}
                              >
                                <span style={threadItemTitle}>
                                  {isActive ? "Current Thread" : `Thread ${index + 1}`}
                                </span>
                                <span style={threadItemMeta}>{label}</span>
                              </button>
                              <button
                                type="button"
                                style={threadItemDeleteButton}
                                onClick={(e) => handleDeleteThreadClick(session.thread_id, e)}
                                title="Delete thread"
                                disabled={isSending}
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div style={{ ...chatMain, ...(isMobile ? chatMainMobile : null) }}>
                  {isSending ? (
                    <div style={pendingState} aria-live="polite">
                      <FaComments size={13} />
                      <span>
                        Thinking... {sendElapsedSeconds}s elapsed
                      </span>
                    </div>
                  ) : null}

                  <div style={messagesPanel}>
                    {historyLoading ? <p style={helperText}>Loading history...</p> : null}

                    {!historyLoading && messages.length === 0 ? (
                      <div style={emptyState}>
                        <p>No messages yet for this configuration.</p>
                        <p>Start with a short greeting.</p>
                      </div>
                    ) : null}

                    {messages.map((message, index) => {
                      const isUser = message.role === "user";
                      const bubbleContent = message.pending
                        ? `Thinking... ${sendElapsedSeconds}s elapsed`
                        : message.content;
                      return (
                        <div
                          key={message.id || `${message.role}-${index}-${message.created_at || "na"}`}
                          style={{
                            ...bubbleRow,
                            justifyContent: isUser ? "flex-end" : "flex-start"
                          }}
                        >
                          <div
                            style={{
                              ...bubble,
                              ...(isUser ? userBubble : assistantBubble)
                            }}
                          >
                            <div style={bubbleHeader}>
                                <span style={bubbleRole}>
                                  {isUser ? "You" : selectedAgent.name}
                                </span>
                              {message.created_at && (
                                <span style={{ ...bubbleTime, ...(isUser ? userTime : assistantTime) }}>
                                  {formatDate(message.created_at)}
                                </span>
                              )}
                            </div>
                            <div style={{ ...bubbleText, ...(message.pending ? pendingBubbleText : null) }}>
                              {bubbleContent}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ ...composer, ...(isMobile ? composerMobile : null) }}>
                    <textarea
                      ref={textareaRef}
                      style={composerInput}
                      rows={1}
                      value={messageInput}
                      onChange={handleInput}
                      onKeyDown={onComposerKeyDown}
                      placeholder="Type your message..."
                    />
                    <button
                      type="button"
                      style={{
                        ...sendButton,
                        ...(canSend ? null : disabledButton)
                      }}
                      disabled={!canSend}
                      onClick={handleSendMessage}
                      aria-label="Send Message"
                    >
                      <FaPaperPlane size={14} style={{ position: "relative", left: "-1px", top: "1px" }} />
                    </button>
                  </div>

                  {chatError ? <p style={errorText}>{chatError}</p> : null}
                </div>
              </div>

              {threadToDelete && (
                <div style={modalOverlay}>
                  <div style={modalCard}>
                    <h3 style={modalTitle}>Delete Thread</h3>
                    <p style={modalText}>
                      Are you sure you want to delete this thread? This action cannot be undone.
                    </p>
                    <div style={modalActions}>
                      <button
                        type="button"
                        style={modalCancelButton}
                        onClick={cancelDeleteThread}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        style={modalDeleteButton}
                        onClick={confirmDeleteThread}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </PatientSidebarShell>
  );
};

export default AgentsPage;

const pageHeader = {
  marginBottom: "18px"
};

const title = {
  margin: 0,
  color: "#0E5580",
  fontSize: "28px",
  fontWeight: 600
};

const subtitle = {
  marginTop: "8px",
  color: "#4f6475"
};

const configsLayout = {
  display: "grid",
  gap: "16px"
};

const card = {
  background: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  border: "1px solid #dce7f0",
  padding: "20px"
};

const sectionTitle = {
  marginTop: 0,
  marginBottom: "8px",
  color: "#0E5580",
  fontSize: "20px",
  fontWeight: 600
};

const helperText = {
  marginTop: 0,
  color: "#5d7182",
  fontSize: "14px"
};

const importControls = {
  display: "flex",
  gap: "10px",
  alignItems: "center",
  flexWrap: "wrap"
};

const primaryButton = {
  background: "#0E5580",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  padding: "10px 14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 600
};

const statusText = {
  marginTop: "10px",
  marginBottom: 0,
  color: "#3c5568"
};

const successText = {
  marginTop: "10px",
  marginBottom: 0,
  color: "#1f7a33"
};

const errorText = {
  marginTop: "10px",
  marginBottom: 0,
  color: "#c0392b"
};

const agentGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "12px",
  marginTop: "12px"
};

const agentCard = {
  border: "1px solid #d8e4ee",
  borderRadius: "10px",
  background: "#fdfefe",
  overflow: "hidden"
};

const selectedAgentCard = {
  borderColor: "#0E5580",
  boxShadow: "0 6px 14px rgba(14, 85, 128, 0.12)"
};

const agentMainButton = {
  width: "100%",
  boxSizing: "border-box",
  border: "none",
  background: "transparent",
  textAlign: "left",
  padding: "12px",
  cursor: "pointer"
};

const agentHeadingRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "8px",
  alignItems: "center"
};

const agentName = {
  margin: 0,
  fontSize: "18px",
  color: "#12344c"
};

const agentDescription = {
  marginTop: "8px",
  marginBottom: "8px",
  color: "#4f6475",
  fontSize: "14px"
};

const agentMetaRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  fontSize: "12px",
  color: "#5c6f7f"
};

const agentActions = {
  borderTop: "1px solid #e3ecf3",
  display: "flex",
  justifyContent: "space-between",
  gap: "8px",
  padding: "10px 12px"
};

const openChatButton = {
  ...primaryButton,
  padding: "8px 12px",
  fontSize: "13px"
};

const deleteButton = {
  background: "#fff2f1",
  color: "#b93b2f",
  border: "1px solid #f0c9c4",
  borderRadius: "8px",
  padding: "8px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontWeight: 600,
  fontSize: "13px"
};

const chatShell = {
  background: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
  border: "1px solid #dce7f0",
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 120px)",
  minHeight: "400px",
  overflow: "hidden"
};

const chatHeader = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  alignItems: "start",
  gap: "16px",
  background: "#f9fbfd",
  borderBottom: "1px solid #dce7f0",
  padding: "16px 20px"
};

const backButton = {
  background: "#ffffff",
  color: "#0E5580",
  border: "1px solid #cfe1ef",
  borderRadius: "8px",
  padding: "8px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 600,
  fontSize: "13px",
  marginTop: "2px",
  transition: "all 0.2s ease"
};

const chatContext = {
  display: "flex",
  flexDirection: "column",
  gap: "4px"
};

const chatTitleRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const chatTitle = {
  margin: 0,
  color: "#0E5580",
  fontSize: "20px",
  fontWeight: "700"
};

const chatSubtitle = {
  margin: 0,
  color: "#5d7182",
  fontSize: "14px"
};

const statusBadge = {
  background: "#e8f2f8",
  color: "#184f73",
  borderRadius: "6px",
  padding: "4px 8px",
  fontSize: "11px",
  fontWeight: "600",
  border: "1px solid #cce1ef"
};

const chatBody = {
  display: "flex",
  flexDirection: "row",
  flex: 1,
  padding: "16px 20px",
  gap: "20px",
  overflow: "hidden"
};

const chatBodyMobile = {
  flexDirection: "column",
  padding: "12px 16px",
  gap: "12px"
};

const chatSidebar = {
  display: "flex",
  flexDirection: "column",
  width: "280px",
  gap: "16px",
  flexShrink: 0,
  overflowY: "auto",
  paddingRight: "4px"
};

const chatSidebarMobile = {
  width: "100%",
  flexShrink: 0,
  overflowY: "visible",
  paddingRight: 0
};

const chatMain = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: "12px",
  overflow: "hidden",
  minWidth: 0
};

const chatMainMobile = {
  flex: 1
};

const pendingState = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "flex-start",
  background: "#eef6fc",
  color: "#154d72",
  border: "1px solid #c7dff1",
  borderRadius: "999px",
  padding: "6px 10px",
  fontSize: "12px",
  fontWeight: 600
};

const chatControls = {
  display: "flex",
  flexDirection: "column",
  gap: "12px"
};

const fieldLabel = {
  display: "grid",
  gap: "6px",
  fontSize: "12px",
  color: "#3a566b",
  fontWeight: 600
};

const textInput = {
  border: "1px solid #cddde9",
  borderRadius: "8px",
  padding: "10px",
  fontSize: "14px",
  boxSizing: "border-box"
};

const helperPill = {
  background: "#f6fbff",
  border: "1px dashed #b8d3e6",
  borderRadius: "8px",
  padding: "10px",
  color: "#34586f",
  fontSize: "13px"
};

const threadActionsRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  flexWrap: "wrap"
};

const secondaryActionButton = {
  background: "#ffffff",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "14px"
};

const threadMetaText = {
  color: "#5d7182",
  fontSize: "12px",
  fontWeight: 600
};

const threadsPanel = {
  border: "1px solid #dce7f0",
  borderRadius: "10px",
  background: "#ffffff",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
  minHeight: 0
};

const threadsPanelMobile = {
  flex: "none",
  maxHeight: "150px"
};

const threadsPanelHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0
};

const threadsPanelTitle = {
  color: "#1b4f72",
  fontSize: "13px",
  fontWeight: 700
};

const threadsList = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflowY: "auto",
  paddingRight: "2px",
  flex: 1,
  minHeight: 0
};

const threadItemWrapper = {
  display: "flex",
  alignItems: "center",
  border: "1px solid #d6e4ef",
  background: "#f9fbfd",
  borderRadius: "10px",
  overflow: "hidden"
};

const threadItemWrapperActive = {
  borderColor: "#0E5580",
  background: "#edf6fc",
  boxShadow: "inset 0 0 0 1px rgba(14, 85, 128, 0.08)"
};

const threadItemMainButton = {
  flex: 1,
  textAlign: "left",
  border: "none",
  background: "transparent",
  padding: "8px 10px",
  cursor: "pointer",
  display: "grid",
  gap: "2px"
};

const threadItemDeleteButton = {
  border: "none",
  background: "transparent",
  color: "#b93b2f",
  padding: "8px 10px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0.7,
  transition: "opacity 0.2s"
};

const threadItemTitle = {
  color: "#12344c",
  fontSize: "12px",
  fontWeight: 700
};

const threadItemMeta = {
  color: "#5d7182",
  fontSize: "12px"
};

const messagesPanel = {
  flex: 1,
  overflowY: "auto",
  border: "1px solid #dce7f0",
  borderRadius: "10px",
  background: "#f9fbfd",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  minHeight: 0 // critical for flex scrolling
};

const emptyState = {
  color: "#5d7182",
  textAlign: "center",
  paddingTop: "22px"
};

const bubbleRow = {
  display: "flex",
  marginBottom: "10px"
};

const bubble = {
  maxWidth: "78%",
  borderRadius: "10px",
  padding: "10px 12px"
};

const userBubble = {
  background: "#0E5580",
  color: "#ffffff"
};

const assistantBubble = {
  background: "#ffffff",
  border: "1px solid #d3e2ee",
  color: "#233d50"
};

const bubbleHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "4px",
  gap: "12px"
};

const bubbleRole = {
  fontSize: "12px",
  fontWeight: 700,
  opacity: 0.9
};

const bubbleTime = {
  fontSize: "11px",
  opacity: 0.7
};

const userTime = {
  color: "#d0e4f2"
};

const assistantTime = {
  color: "#7e95a8"
};

const bubbleText = {
  whiteSpace: "pre-wrap",
  lineHeight: 1.5
};

const pendingBubbleText = {
  color: "#5d7182",
  fontStyle: "italic"
};

const composer = {
  display: "flex",
  alignItems: "flex-end",
  gap: "12px",
  background: "#ffffff",
  border: "1px solid #cddde9",
  borderRadius: "24px",
  padding: "8px 12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  position: "relative",
  transition: "border-color 0.2s, box-shadow 0.2s"
};

const composerMobile = {
  padding: "6px 10px"
};

const composerInput = {
  flex: 1,
  background: "transparent",
  border: "none",
  padding: "10px 4px 10px 10px",
  fontSize: "15px",
  resize: "none",
  minHeight: "24px",
  maxHeight: "150px",
  color: "#12344c",
  outline: "none",
  lineHeight: "1.5",
  boxSizing: "border-box"
};

const sendButton = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  minWidth: "40px",
  borderRadius: "50%",
  background: "#0E5580",
  color: "#ffffff",
  border: "none",
  boxShadow: "0 2px 6px rgba(14, 85, 128, 0.2)",
  cursor: "pointer",
  transition: "transform 0.2s, opacity 0.2s",
  flexShrink: 0,
  marginBottom: "2px"
};


const disabledButton = {
  opacity: 0.4,
  cursor: "not-allowed",
  boxShadow: "none",
  background: "#a5b9c7"
};

const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(18, 52, 76, 0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: "20px"
};

const modalCard = {
  background: "#ffffff",
  borderRadius: "12px",
  padding: "24px",
  width: "100%",
  maxWidth: "400px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
};

const modalTitle = {
  margin: "0 0 12px 0",
  color: "#0E5580",
  fontSize: "20px"
};

const modalText = {
  margin: "0 0 24px 0",
  color: "#4f6475",
  fontSize: "15px",
  lineHeight: 1.5
};

const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px"
};

const modalCancelButton = {
  background: "#ffffff",
  color: "#5d7182",
  border: "1px solid #cddde9",
  borderRadius: "8px",
  padding: "8px 16px",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "14px"
};

const modalDeleteButton = {
  background: "#c0392b",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  padding: "8px 16px",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "14px"
};
